import { defineStore } from "pinia";
import { client, setBearerToken, extractData } from "@/shared/lib/api";
import type { IUser, IAuthData } from "@/shared/types";

interface AuthState {
    token: string;
    tokenExpires: string;
    user: IUser | null;
    lastLogin: string;
    deviceName: string;
}

export const useAuthStore = defineStore("auth", {
    state: (): AuthState => ({
        token: "",
        tokenExpires: "",
        user: null,
        lastLogin: "",
        deviceName: "",
    }),

    getters: {
        isLoggedIn(): boolean {
            if (!this.token) return false;
            if (!this.tokenExpires) return false;
            const date = new Date(this.tokenExpires);
            const now = new Date();
            return date >= now;
        },
        defaultAreaId(): number | null {
            return this.user?.default_area?.id ?? null;
        },
    },

    actions: {
        isTokenExpired(): boolean {
            if (!this.tokenExpires) return true;
            const date = new Date(this.tokenExpires);
            const now = new Date();
            return date < now;
        },

        async login(phone: string, password: string) {
            const response = await client.post<IAuthData>("/employee/auth/login", { phone, password });
            const authData = extractData(response);

            this.token = authData.token.token;
            this.tokenExpires = authData.token.expires;
            this.user = authData.user;
            this.lastLogin = phone;

            setBearerToken(this.token);

            return authData;
        },

        async logout() {
            try {
                await client.post("/employee/auth/logout");
            } catch {
                // ignore
            }

            this.token = "";
            this.tokenExpires = "";
            this.user = null;
            setBearerToken("");
        },

        async getUser() {
            try {
                const response = await client.get<IUser>("/employee/profile");
                const userData = extractData(response);
                this.user = userData;
                return userData;
            } catch {
                this.token = "";
                this.tokenExpires = "";
                this.user = null;
                setBearerToken("");
                throw new Error("Failed to get user");
            }
        },

        async setDefaultArea(areaId: number | null) {
            if (areaId !== null) {
                const response = await client.put<IUser>("/employee/profile/default-area", null, {
                    params: { area_id: areaId },
                });
                this.user = extractData(response);
            } else {
                const response = await client.delete<IUser>("/employee/profile/default-area");
                this.user = extractData(response);
            }
        },

        async checkAuth() {
            if (this.token && !this.isTokenExpired()) {
                setBearerToken(this.token);
                try {
                    await this.getUser();
                } catch {
                    // token invalid
                }
            }
        },

        async updateToken(token: string, expires: string) {
            this.token = token;
            this.tokenExpires = expires;
            setBearerToken(token);
        },
    },

    persist: true,
});
